import { ReactNode, useMemo } from 'react';

// material-ui
import CssBaseline from '@mui/material/CssBaseline';
import StyledEngineProvider from '@mui/material/StyledEngineProvider';
import { Theme, ThemeOptions, ThemeProvider, createTheme } from '@mui/material/styles';
import { TypographyOptions } from '@mui/material/styles/createTypography';

import useConfig from 'src/hooks/useConfig';
import { CustomShadowProps } from 'src/types/default-theme';

import componentStyleOverrides from './compStyleOverride';
import Palette from './palette';
import customShadows from './shadows';
import Typography from './typography';

// types

interface Props {
    children: ReactNode;
}

// Necessary type defs for the custom typography variants
declare module '@mui/material/styles' {
    interface TypographyVariants {
        pageTitle: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        pageTitle?: React.CSSProperties;
    }

    interface Palette {
        border: {
            primary: string;
            disabled: string;
        };
    }

    interface PalletteOptions {
        border: {
            primary: string;
            disabled: string;
        };
    }
}

declare module '@mui/material/styles/createPalette' {
    export interface PaletteOptions {
        border: PaletteColorOptions;
    }

    export interface Color {
        50: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
        A100: string;
        A200: string;
        A400: string;
        A700: string;
        primary: string;
    }
    export interface Palette {
        border: SimplePaletteColorOptions & ColorPartial;
    }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        pageTitle: true;
    }
}

export default function ThemeCustomization({ children }: Props) {
    const { borderRadius, fontFamily, navType, outlinedFilled, presetColor, rtlLayout } =
        useConfig();

    const theme: Theme = useMemo<Theme>(() => {
        return Palette(navType, presetColor);
    }, [navType, presetColor]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const themeTypography: TypographyOptions = useMemo<TypographyOptions>(() => {
        return Typography(theme, borderRadius, fontFamily);
    }, [theme, borderRadius, fontFamily]);
    const themeCustomShadows: CustomShadowProps = useMemo<CustomShadowProps>(() => {
        return customShadows(navType, theme);
    }, [navType, theme]);

    const themeOptions: ThemeOptions = useMemo(() => {
        return {
            direction: rtlLayout ? 'rtl' : 'ltr',
            palette: theme.palette,
            mixins: {
                toolbar: {
                    minHeight: '48px',
                    padding: '16px',
                    '@media (min-width: 600px)': {
                        minHeight: '48px',
                    },
                },
            },
            typography: themeTypography,
            customShadows: themeCustomShadows,
        };
    }, [rtlLayout, theme, themeCustomShadows, themeTypography]);

    const themes: Theme = createTheme(themeOptions);

    const defaultComponentOverrides = {
        MuiTypography: {
            defaultProps: {
                component: 'div',
            },
        },
    };

    themes.components = useMemo(() => {
        return {
            ...defaultComponentOverrides,
            ...componentStyleOverrides(themes, borderRadius, outlinedFilled),
        };
    }, [themes, borderRadius, outlinedFilled]);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={themes}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </StyledEngineProvider>
    );
}
